/**
 * Routes (§12.3).
 *
 * Phase 1 lands the tunnels surface and onboarding. The routes that remain placeholders —
 * inspector, domains, traffic, logs, access, billing — are still declared so the sidebar's nav
 * is real, and each names the phase it arrives in rather than 404ing.
 *
 * `/`, `/products` and `/docs` are the unguarded branches. Everything else carries `meta.auth`, and the guard
 * below sends an anonymous visitor to `/login` with a redirect back — which is the right
 * behaviour everywhere except on the pages that explain what this is and how to get an account.
 *
 * The Overview lives at `/dashboard`, not `/`. §14.1's landing page took the root (D-036); every
 * other app URL is unchanged.
 */
import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useWorkspaceStore } from '@/stores/workspace'

const routes: RouteRecordRaw[] = [
    {
        path: '/login',
        name: 'login',
        component: () => import('@/pages/auth/LoginPage.vue'),
        meta: { guest: true, title: 'Sign in' },
    },
    {
        path: '/register',
        name: 'register',
        component: () => import('@/pages/auth/RegisterPage.vue'),
        meta: { guest: true, title: 'Create your account' },
    },
    {
        // §12.3: guarded by `auth` only. A user arriving from their terminal mid-onboarding must
        // land here rather than being redirected — the flow they started is the one to finish.
        path: '/device',
        name: 'device-approval',
        component: () => import('@/pages/device/DeviceApprovalPage.vue'),
        meta: { auth: true, title: 'Authorise device' },
    },
    {
        path: '/2fa',
        name: 'two-factor',
        component: () => import('@/pages/auth/TwoFactorPage.vue'),
        meta: { guest: true, title: 'Two-factor authentication' },
    },
    {
        path: '/forgot-password',
        name: 'forgot-password',
        component: () => import('@/pages/auth/ForgotPasswordPage.vue'),
        meta: { guest: true, title: 'Reset your password' },
    },
    {
        path: '/reset-password/:token',
        name: 'reset-password',
        component: () => import('@/pages/auth/ResetPasswordPage.vue'),
        meta: { guest: true, title: 'Choose a new password' },
    },
    {
        path: '/verify-email',
        name: 'verify-email',
        component: () => import('@/pages/auth/VerifyEmailPage.vue'),
        meta: { auth: true, title: 'Verify your email' },
    },
    {
        path: '/oauth/callback',
        name: 'oauth-callback',
        component: () => import('@/pages/auth/OAuthCallbackPage.vue'),
        meta: { title: 'Signing you in' },
    },

    {
        /**
         * §14.4 renders onboarding "inside the marketing chrome (nav + footer), not the app
         * shell". No marketing site exists and D-007 keeps it out of scope, so the distinction
         * being drawn — no app chrome around a first-run flow — is served by a minimal in-SPA
         * shell. Recorded in DECISIONS.md D-024.
         */
        path: '/onboarding',
        component: () => import('@/components/layout/MarketingShell.vue'),
        meta: { auth: true },
        children: [
            {
                path: '',
                name: 'onboarding',
                component: () => import('@/pages/onboarding/OnboardingPage.vue'),
                meta: { title: 'Get started' },
            },
        ],
    },

    {
        /**
         * The docs (§18 item 12), as routes in this SPA rather than §12.1's separate Nuxt site —
         * D-024 clause 10.
         *
         * A third `MarketingShell` parent, and **deliberately without `meta: { auth: true }`**: the
         * existing one carries it, and installation instructions that bounce an anonymous reader to
         * `/login` are instructions nobody can follow. These are the only public routes here.
         */
        path: '/docs',
        component: () => import('@/components/layout/MarketingShell.vue'),
        children: [
            {
                path: '',
                component: () => import('@/pages/docs/DocsLayout.vue'),
                children: [
                    // Landing on the first page rather than on an index that only lists five links.
                    { path: '', redirect: '/docs/installation' },
                    {
                        path: 'installation',
                        name: 'docs-installation',
                        component: () => import('@/pages/docs/InstallationPage.vue'),
                        meta: { title: 'Installation' },
                    },
                    {
                        path: 'quick-start',
                        name: 'docs-quick-start',
                        component: () => import('@/pages/docs/QuickStartPage.vue'),
                        meta: { title: 'Quick Start' },
                    },
                    {
                        path: 'http',
                        name: 'docs-http',
                        component: () => import('@/pages/docs/HttpCommandPage.vue'),
                        meta: { title: 'nlocal http' },
                    },
                    {
                        path: 'login',
                        name: 'docs-login',
                        component: () => import('@/pages/docs/LoginCommandPage.vue'),
                        meta: { title: 'nlocal login' },
                    },
                    {
                        path: 'nlocal-yml',
                        name: 'docs-nlocal-yml',
                        component: () => import('@/pages/docs/TunnelFilePage.vue'),
                        meta: { title: 'nlocal.yml' },
                    },
                ],
            },
        ],
    },

    {
        /**
         * §14.1's marketing landing, and the last of Phase 1's kit screens (D-036).
         *
         * A fourth `MarketingShell` parent, public like `/docs` and for the same reason: this is
         * the page for people who do not have an account. Until it existed `/` was the
         * authenticated Overview and the guard below sent an anonymous visitor to `/login`, so the
         * front door of the product was a sign-in form.
         */
        path: '/',
        component: () => import('@/components/layout/MarketingShell.vue'),
        children: [
            {
                path: '',
                name: 'landing',
                component: () => import('@/pages/marketing/LandingPage.vue'),
                meta: { title: 'Expose your localhost. Instantly.' },
            },
            {
                /**
                 * §14.4's kit screen as a public walkthrough, and where the nav's **Product** entry goes.
                 *
                 * It used to be `#product`, an anchor into the landing hero — inert on `/` and, on any
                 * other page, pointing at an element that is not there. `/onboarding` could not take its
                 * place: that route is `meta.auth` and the guard below sends an onboarded workspace to
                 * `/dashboard`, so nobody clicking Product would have reached it.
                 *
                 * Public, deliberately: no `meta.auth`, on the same `MarketingShell` record as `/` and for
                 * the same reason the docs are public — a page explaining what this is cannot require an
                 * account to read.
                 */
                path: 'products',
                name: 'products',
                component: () => import('@/pages/marketing/ProductsPage.vue'),
                meta: { title: 'Product' },
            },
        ],
    },

    {
        /**
         * The app shell keeps `path: '/'` so every screen below stays at the URL it already had —
         * `/tunnels`, `/inspector`, `/settings`. It has **no index child**: `/` itself is matched
         * by the landing record above, and the Overview moved to `/dashboard` to make room.
         *
         * Two records may share a parent path when their resolved children do not collide, which
         * these do not. The alternative — moving the whole shell to `/dashboard` — would have
         * renamed every app URL to buy nothing.
         */
        path: '/',
        component: () => import('@/components/layout/AppShell.vue'),
        meta: { auth: true },
        children: [
            {
                path: 'dashboard',
                name: 'overview',
                component: () => import('@/pages/app/OverviewPage.vue'),
                meta: { title: 'Overview' },
            },
            // Declared so the shell's navigation is real. Each lands with its phase.
            {
                path: 'tunnels',
                name: 'tunnels',
                component: () => import('@/pages/app/TunnelsPage.vue'),
                meta: { title: 'Tunnels' },
            },
            {
                // §12.3: the detail tabs are child routes, not local state — a tab is a place, and
                // one someone should be able to link a colleague to.
                path: 'tunnels/:id',
                component: () => import('@/pages/app/TunnelDetailPage.vue'),
                meta: { title: 'Tunnel' },
                children: [
                    {
                        path: '',
                        name: 'tunnel',
                        component: () => import('@/pages/app/tunnel/TunnelOverviewTab.vue'),
                    },
                    {
                        path: 'requests',
                        name: 'tunnel-requests',
                        component: () => import('@/pages/app/tunnel/TunnelRequestsTab.vue'),
                        meta: { title: 'Tunnel requests' },
                    },
                    {
                        path: 'traffic',
                        name: 'tunnel-traffic',
                        component: () => import('@/pages/app/tunnel/TunnelTrafficTab.vue'),
                        meta: { title: 'Tunnel traffic' },
                    },
                    {
                        path: 'logs',
                        name: 'tunnel-logs',
                        component: () => import('@/pages/app/tunnel/TunnelLogsTab.vue'),
                        meta: { title: 'Tunnel logs' },
                    },
                    {
                        // Phase 3 delivered the configurable surface a tunnel actually has, and
                        // it is not here: §14.12's settings belong to a *domain*, one at a time,
                        // and live on the Domains screen because `PATCH /domains/{id}/settings`
                        // takes one id. M7 built the Access tab beside this one, which is where
                        // §14.14's per-tunnel rules went; what a Configuration tab would still
                        // show is the tunnel's own config, and that stays with Phase 4.
                        path: 'configuration',
                        name: 'tunnel-configuration',
                        component: () => import('@/pages/app/ComingSoonPage.vue'),
                        meta: { title: 'Tunnel configuration', phase: 'Phase 4' },
                    },
                    {
                        path: 'access',
                        name: 'tunnel-access',
                        component: () => import('@/pages/app/tunnel/TunnelAccessTab.vue'),
                        meta: { title: 'Tunnel access' },
                    },
                ],
            },
            {
                path: 'inspector',
                name: 'inspector',
                component: () => import('@/pages/app/InspectorPage.vue'),
                meta: { title: 'Request Inspector' },
            },
            {
                path: 'endpoints',
                name: 'endpoints',
                component: () => import('@/pages/app/EndpointsPage.vue'),
                meta: { title: 'Endpoints' },
            },
            {
                path: 'domains',
                name: 'domains',
                component: () => import('@/pages/app/DomainsPage.vue'),
                meta: { title: 'Domains & DNS' },
            },
            {
                path: 'traffic',
                name: 'traffic',
                component: () => import('@/pages/app/TrafficPage.vue'),
                meta: { title: 'Traffic & Analytics' },
            },
            {
                path: 'logs',
                name: 'logs',
                component: () => import('@/pages/app/LogsPage.vue'),
                meta: { title: 'Logs' },
            },
            {
                // §12.3: the Access area's tabs are child routes, like the tunnel detail's — a tab
                // is a place. SSO / SCIM keeps the placeholder that names its phase (§14.30: P6).
                path: 'settings/access',
                component: () => import('@/pages/app/AccessPage.vue'),
                meta: { title: 'Access Policies' },
                children: [
                    { path: '', name: 'access-policies', redirect: { name: 'access-rbac' } },
                    {
                        path: 'rbac',
                        name: 'access-rbac',
                        component: () => import('@/pages/app/access/AccessRbacTab.vue'),
                        meta: { title: 'Access Policies · RBAC' },
                    },
                    {
                        path: 'gates',
                        name: 'access-gates',
                        component: () => import('@/pages/app/access/AccessGatesTab.vue'),
                        meta: { title: 'Access Policies · Ingress gates' },
                    },
                    {
                        path: 'perimeter',
                        name: 'access-perimeter',
                        component: () => import('@/pages/app/access/AccessPerimeterTab.vue'),
                        meta: { title: 'Access Policies · Perimeter' },
                    },
                    {
                        path: 'sso',
                        name: 'access-sso',
                        component: () => import('@/pages/app/ComingSoonPage.vue'),
                        meta: { title: 'SSO / SCIM Directory Sync', phase: 'Phase 6' },
                    },
                    {
                        path: 'geo',
                        name: 'access-geo',
                        component: () => import('@/pages/app/access/AccessGeoTab.vue'),
                        meta: { title: 'Access Policies · Geo-fencing' },
                    },
                ],
            },
            {
                path: 'settings/tokens',
                name: 'tokens',
                component: () => import('@/pages/app/TokensPage.vue'),
                meta: { title: 'API Keys & CLI Tokens' },
            },
            {
                path: 'settings/billing',
                name: 'billing',
                component: () => import('@/pages/app/BillingPage.vue'),
                meta: { title: 'Usage & Billing' },
            },
            {
                path: 'notifications',
                name: 'notifications',
                component: () => import('@/pages/app/NotificationsPage.vue'),
                meta: { title: 'Notifications' },
            },
            {
                // Tabs as child routes, like Access. The empty child renders Profile rather than
                // redirecting, so `/settings` keeps the URL every existing link uses.
                path: 'settings',
                component: () => import('@/pages/app/SettingsPage.vue'),
                meta: { title: 'Settings' },
                children: [
                    {
                        path: '',
                        name: 'settings',
                        component: () => import('@/pages/app/settings/SettingsProfileTab.vue'),
                        meta: { title: 'Settings' },
                    },
                    {
                        path: 'profile',
                        name: 'settings-profile',
                        component: () => import('@/pages/app/settings/SettingsProfileTab.vue'),
                        meta: { title: 'Settings · Profile' },
                    },
                    {
                        path: 'security',
                        name: 'settings-security',
                        component: () => import('@/pages/app/settings/SettingsSecurityTab.vue'),
                        meta: { title: 'Settings · Security' },
                    },
                    {
                        path: 'cli',
                        name: 'settings-cli',
                        component: () => import('@/pages/app/settings/SettingsCliTab.vue'),
                        meta: { title: 'Settings · CLI Devices' },
                    },
                    {
                        path: 'notifications',
                        name: 'settings-notifications',
                        component: () => import('@/pages/app/settings/SettingsNotificationsTab.vue'),
                        meta: { title: 'Settings · Notifications' },
                    },
                    {
                        path: 'workspace',
                        name: 'settings-workspace',
                        component: () => import('@/pages/app/settings/SettingsWorkspaceTab.vue'),
                        meta: { title: 'Settings · Workspace' },
                    },
                    {
                        path: 'webhooks',
                        name: 'settings-webhooks',
                        component: () => import('@/pages/app/settings/SettingsWebhooksTab.vue'),
                        meta: { title: 'Settings · Webhooks' },
                    },
                    {
                        path: 'danger',
                        name: 'settings-danger',
                        component: () => import('@/pages/app/settings/SettingsDangerTab.vue'),
                        meta: { title: 'Settings · Danger Zone' },
                    },
                    {
                        path: 'members',
                        name: 'settings-members',
                        component: () => import('@/pages/app/settings/SettingsMembersTab.vue'),
                        meta: { title: 'Settings · Members' },
                    },
                ],
            },
        ],
    },

    {
        // Neither `auth` nor `guest`: an invitee may be signed out, signed in, or signed in as
        // somebody else, and the page says which (§14.18). The token is the credential.
        path: '/invite/:token',
        name: 'invite',
        component: () => import('@/pages/InvitePage.vue'),
        meta: { title: 'Invitation' },
    },

    {
        path: '/:pathMatch(.*)*',
        name: 'not-found',
        component: () => import('@/pages/NotFoundPage.vue'),
        meta: { title: 'Not found' },
    },
]

export const router = createRouter({
    history: createWebHistory(),
    routes,
    scrollBehavior: (_to, _from, saved) => saved ?? { top: 0 },
})

router.beforeEach(async (to) => {
    const auth = useAuthStore()

    // One session probe per page load. Guards on every route would otherwise each fire their
    // own /v1/me on the first navigation.
    if (!auth.resolved) {
        await auth.fetchUser()
    }

    /**
     * Seed the workspace store from whatever the auth store knows, on every navigation where it
     * is still empty.
     *
     * Not only after `fetchUser`. Signing in sets the user without touching this store, and
     * `auth.resolved` is already true by then — from the probe on the login page that came back
     * unauthenticated. So the first navigation after a login used to run with no workspace at
     * all, which left `current` null and made any guard that reads workspace state silently
     * fall through. That is how `!workspace.onboarded` (§12.3) failed to send a new user to
     * onboarding: the flag was not missing, the workspace was.
     */
    if (auth.isAuthenticated) {
        const workspaces = useWorkspaceStore()
        if (workspaces.workspaces.length === 0) {
            workspaces.setWorkspaces(auth.user?.workspaces ?? [])
        }
    }

    if (to.meta.auth && !auth.isAuthenticated) {
        // Remember where they were headed so the post-login redirect is not always the overview.
        // `/dashboard` is where login lands anyway, so carrying it as a query parameter would put
        // a redirect in the URL that changes nothing. `/` is no longer a case here at all — it is
        // the public landing page and never reaches this branch.
        return {
            name: 'login',
            query: to.fullPath === '/dashboard' ? {} : { redirect: to.fullPath },
        }
    }

    if (to.meta.guest && auth.isAuthenticated) {
        return { name: 'overview' }
    }

    /**
     * §12.3 guards `/onboarding` with `!workspace.onboarded`.
     *
     * Both directions matter. Without the second, the screen is unreachable by the only person
     * it exists for — someone whose workspace has never been set up lands on an Overview full of
     * empty states with no route to the flow that fills them.
     */
    if (auth.isAuthenticated) {
        const workspaces = useWorkspaceStore()
        const onboarded = workspaces.current?.onboarded

        if (to.name === 'onboarding' && onboarded === true) {
            return { name: 'overview' }
        }
        if (to.name === 'overview' && onboarded === false) {
            return { name: 'onboarding' }
        }
    }

    return true
})

router.afterEach((to) => {
    const title = to.meta.title as string | undefined
    document.title = title ? `${title} · nlocal` : 'nlocal'
})
